/**
 * The exercise library, beside the day being programmed.
 *
 * ── It is a SHELL around `entities/exercise-library`'s list since 17 September 2026 ───────────
 * The search field, the filter glyph and its four-tab panel, the active-selection chips, the count
 * row, the four states and the row itself were all this file's until the owner asked that the survey
 * editor's recording picker be the same library in a smaller form („wyciągnij ją do wspólnego
 * komponentu, filtry i szukajka 1:1"). They moved DOWN, whole, into the entity that already owned
 * every decision behind them.
 *
 * What is left here is everything that is about the DAY rather than about the catalogue, and each of
 * these is a thing no other host has:
 *
 * · the eyebrow naming the session or the movement a warm-up is FOR, the title and the ✕;
 * · the day-affinity default and the „Pasują do dnia Push A" caption that lifts it;
 * · the insert itself — the press, the warm-up press and the foot's batch add — with its refusals;
 * · the multi-select foot, the Escape ladder and the live-region announcements;
 * · the DRAG, whose payload is a plan draft's `ExercisePick` (`RailDraggableRow`);
 * · the hover peek.
 *
 * ── The chrome is the 02.08 KANON gallery's, at both widths ──────────────────────────────────
 * `Dzien - pelny ekran` draws the rail as stacked blocks — head, filter panel, list, foot — and the
 * head itself as three rows: the title, the search field with the filter disclosure beside it, and
 * the row of active selections. `Tydzien z panelem dnia` draws the 240px variant, which keeps the
 * title, the field and the list and drops everything else.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ExerciseLibraryList,
  toMuscleAttribution,
  useExerciseLibraryQuery,
  type ExerciseFacetLabels,
  type ExerciseLibraryListLabels,
  type ExerciseListItem,
} from '~/entities/exercise-library';
import {
  addExercisesFromLibrary,
  dayCard,
  insertWarmupFor,
  runMoveCommand,
  useDayDraft,
  type AuthorableExerciseRole,
} from '~/entities/plan-draft';
import { useSessionUser } from '~/entities/session';
import { displayInitials } from '~/shared/lib/displayInitials';
import inkButton from '~/shared/ui/inkButton.module.css';

import { dayAffinity } from '../model/dayAffinity';
import {
  libraryPick,
  toggleLibrarySelection,
  type LibrarySelection,
} from '../model/librarySelection';

import { RailDraggableRow } from './RailDraggableRow';
import { RailPeek } from './RailPeek';

import styles from './ExerciseRail.module.css';

export interface ExerciseRailProps {
  /** The day an insert lands in. `null` while no day is open — the rail then renders nothing. */
  readonly daySlotToken: string | null;
  /**
   * Which block a PRESSED row lands in: the main work, or the day's warm-up run. Set by whichever
   * dashed zone the trainer pressed to ask for the library.
   *
   * A DRAGGED row ignores it — the zone that receives the drop decides, since it is the only thing
   * that knows where the pointer went. See `canDrag` below.
   */
  readonly role: AuthorableExerciseRole;
  /**
   * The exercise a PRESSED row becomes the warm-up OF — the card head's `Rozgrzewka` chip, and the
   * mini-group behind it.
   *
   * `null` for every other reach, which is the two block pills and every drag. When it is set the
   * press routes through `insertWarmupFor` instead of the library insert: the pick lands immediately
   * above that one lift, sharing a fresh bracket with it, rather than joining the day's own warm-up
   * run. `role` is ignored while it is set, because the act names its own role.
   *
   * A DRAGGED row ignores it for the same reason it ignores `role` — the zone that receives the drop
   * is the only thing that knows where the pointer went.
   */
  readonly warmupTargetSlotToken?: string | null;
  /**
   * Changes every time the host asks for the rail — pressing "+ Dodaj ćwiczenie" or "+ Pozycja
   * rozgrzewki". The search field takes focus on each change, so the affordance that switched the
   * dock to fullscreen lands the trainer in the field they were reaching for.
   */
  readonly focusSignal: number;
  readonly announce: (message: string) => void;
  /**
   * The exercise an ADD just created, handed back to the host. Optional, and one host uses it.
   *
   * The builder's panel is the reason it exists: its library is a drawer laid modally OVER the day,
   * so the successful add is also the end of the reach — the drawer has to go away, and the row the
   * trainer pressed goes away with it. Inside a focus trap that leaves focus on `<body>` and the
   * next Tab restarts at the top, so the host is told which card was made and sends focus there.
   *
   * **All three routes fire it**: the immediate press, the warm-up press, and the foot's batch add,
   * which names the first card of the batch. The batch was the exception for one day and the owner
   * withdrew it on 16 September 2026 — see `addSelection`.
   *
   * The two hosts that draw the library BESIDE the day (the fullscreen modal, the calendar's 240px
   * column) pass nothing: the row they pressed is still under their finger and still holds focus.
   */
  readonly onInserted?: (exerciseSlotToken: string) => void;
  /**
   * Put the library away. The DRAWER's own ✕, which the mock draws in its header.
   *
   * `undefined` in the two hosts that stand the library beside the day: a column that is always
   * there has nothing to close. The drawer's scrim dismisses too — this is the second path the mock
   * draws, and it is the one a pointer aims at.
   */
  readonly onClose?: () => void;
  /**
   * The library stands OVER the day it edits, so nothing behind it can take a drop.
   *
   * A property of the HOST and not of the variant, which is what it was keyed on until 19 September
   * 2026. The builder's drawer slides over the day panel and marks it `inert`: a row lifted out of
   * it is carried over a surface that registers no drop target, the release lands on the scrim, and
   * the movement is nowhere — so it offers no drag and the press is the whole path. The calendar's
   * dock stands the same drawer OUTSIDE itself, to the LEFT, where the day form is fully visible
   * and takes a drop exactly as it did when that column was 240px wide. Two hosts, two answers, and
   * the variant is not the question.
   */
  readonly overlaysTheDay?: boolean;
  /**
   * Whether the library is ON SCREEN. The builder keeps its drawer mounted while it is put away, so
   * the catalogue is warm when it slides out; the search's second-language read waits for this.
   * Defaults to `true` — the calendar dock's column is on screen whenever it is mounted.
   */
  readonly isShown?: boolean;
}

const KEY = 'web:planBuilder.rail';

export function ExerciseRail({
  daySlotToken,
  role,
  warmupTargetSlotToken = null,
  focusSignal,
  announce,
  onInserted,
  overlaysTheDay = false,
  onClose,
  isShown = true,
}: ExerciseRailProps) {
  const { t } = useTranslation();
  const library = useExerciseLibraryQuery({ shown: isShown });
  // Who is signed in — the `Źródło` facet marks "Moje" with the trainer's own avatar, and falls back
  // to their initials when they have no photograph. Read here rather than threaded in: the rail
  // already reads the store for the open day, and no host could supply a better answer.
  const user = useSessionUser();
  // The workout this reach is FOR — the drawer's eyebrow names it, because the drawer lies over the
  // day it is adding to and the day's own head is behind it. Read through the entity's own narrow
  // hook and its own projection, so the name here and the name on the card are one answer.
  const openDay = useDayDraft(daySlotToken ?? '');
  // True once the day-fit caption has been pressed and the whole library is being offered. Its own
  // `useState`, deliberately not part of `ExerciseQuery`: the day default is not a filter the
  // trainer set, it is the state this ONE host opens in, and folding it into the entity's query type
  // would put a drawer-only concern into a shape the calendar and the survey editor also read.
  const [isDayDefaultLifted, setDayDefaultLifted] = useState(false);
  // The movement the pointer is over, and whether its still failed to load — ONE piece of state,
  // because they move together: a different row is a different photograph, so carrying the failure
  // separately would let one broken image blank the next movement's card. The peek is a single
  // element that re-fills rather than a card per row, so the flag cannot live inside it.
  const [peek, setPeek] = useState<{ id: number; stillFailed: boolean } | null>(null);
  // The movements chosen but not yet added, and the REACH they were chosen during — one piece of
  // state, because a selection only means anything inside the reach that built it. The signal
  // changes every time the host asks for the library again, and comparing it here is what drops a
  // stale selection without an effect: a trainer who opens the catalogue for Monday, chooses three
  // and closes it must not find those three still ticked when they open it for Tuesday.
  const [chosen, setChosen] = useState<{ signal: number; picks: LibrarySelection }>({
    signal: focusSignal,
    picks: [],
  });

  // The drawer is the mock's own surface and carries the full chrome at 320px: an eyebrow, the full
  // title, its own ✕, the filter control beside the search and a count row. The calendar's 240px
  // column keeps the reduced set the 02.08 gallery draws for it.
  const card = openDay === null ? null : dayCard(openDay);
  const dayName = card === null ? null : (card.title ?? t('web:planBuilder.untitledSession'));

  // **The library opens on the day's own groups.** The mock's rule, in `model/dayAffinity` — see its
  // header for what it is and for the two ways out of it. It is the DRAWER's alone: the calendar's
  // 240px column stands BESIDE the day rather than over it, it is a surface a trainer drags out of,
  // and narrowing it would change a host the 07.09 handoff never redrew.
  const affinity = dayAffinity({
    results: library.catalogue.results,
    counted: card?.countedExercises ?? [],
    // Built from the SAME read the results come out of, so the day's load and the library's offer
    // can never be computed from two different catalogues.
    attribution: toMuscleAttribution(library.items),
    activeFilterCount: library.catalogue.activeCount,
    searchText: library.query.text,
    isLifted: isDayDefaultLifted,
  });
  const isDayDefaultOn = affinity.isApplied;
  const results = isDayDefaultOn ? affinity.results : library.catalogue.results;
  // The MOVEMENT this reach is aimed at, when the card head's `Rozgrzewka` chip opened the drawer.
  // The mock says so twice — "ROZGRZEWKA DO: WYCISKANIE SZTANGI LEŻĄC" over the title, and a footer
  // naming what a press does — and this build said neither: it printed "Dodaj do dnia · Push A"
  // and "Kliknij ćwiczenie, aby dodać je do dnia" over a catalogue whose next press would land a
  // warm-up inside one exercise. The insert was already right; the two sentences describing it were
  // not.
  //
  // Read out of the day already open rather than through a second store subscription: the token is
  // one of this day's exercises by construction, and `openDay` is the node the rail has.
  const warmupTargetName =
    warmupTargetSlotToken === null || openDay === null
      ? null
      : (openDay.exercises.find((exercise) => exercise.slotToken === warmupTargetSlotToken)?.name ??
        null);

  /**
   * **A press CHOOSES here; the foot adds what was chosen** (owner, 16 September 2026: "dodaj do
   * side panelu dodawania ćwiczeń opcję dodania kilku ćwiczeń naraz, a nie że za każdym razem
   * trzeba się przeklikiwać").
   *
   * **Choosing is what changed; what happens AFTER the add did not.** A successful batch still ends
   * the reach and puts the catalogue away — see `addSelection`, and the owner's correction of the
   * same day. The affordance is "pick five in one visit", not "keep the drawer out".
   *
   * Two surfaces keep the immediate insert, and both for a reason of their own:
   *
   * · **The calendar's 240px column** stands BESIDE the day rather than over it, so nothing there
   *   is in the way of the thing it just changed. It is also 240px wide, where a foot carrying a
   *   hint, a `Wyczyść` and a primary does not fit.
   * · **A drawer opened by an exercise's `Rozgrzewka` chip.** That act is "give this movement its
   *   warm-up", and `insertWarmupFor` REPLACES rather than stacks — so choosing three and adding
   *   them would leave one, silently. One movement is the whole act there.
   *
   * A drawer with NO day open is neither: there is nowhere for a batch to land, so the foot is not
   * drawn at all rather than standing there as a control that silently does nothing.
   */
  const isSelecting = warmupTargetSlotToken === null && daySlotToken !== null;
  const selection = chosen.signal === focusSignal ? chosen.picks : [];

  const choose = (item: ExerciseListItem) => {
    setChosen({ signal: focusSignal, picks: toggleLibrarySelection(selection, libraryPick(item)) });
  };

  const clearSelection = () => {
    setChosen({ signal: focusSignal, picks: [] });
  };

  /**
   * Add everything chosen, in one act — ONE store operation, so it is one patch, one dirty flag and
   * one undo entry, and so a target that refuses takes the whole batch with it rather than leaving
   * the day half filled.
   *
   * ── A successful add ENDS THE REACH, and the drawer goes (owner, 16 September 2026) ──────────
   * It stayed open for one day, on the reading that "a nie że za każdym razem trzeba się
   * przeklikiwać" meant the catalogue should be there for the next batch too. **Owner: "jak klikam
   * dodaj ćwiczenie to biblioteka powinna się zamykać a się nie zamyka o co chodzi".** What he asked
   * for was the clicking THROUGH — one press per movement with a re-opened drawer between each —
   * and choosing five in one visit already solves that. The 7 September rule stands unchanged
   * underneath it: the drawer lies modally over the day it just changed, so leaving it there hides
   * the answer, and the row the trainer pressed is about to unmount.
   *
   * `onInserted` is therefore fired for the batch exactly as it is for the single press, naming the
   * FIRST card of the batch — the one the trainer will read first, and the only one of the several
   * that a single focus target can be. Without it the host closes nothing and focus lands on
   * `<body>` inside a trap, where the next Tab restarts at the top of the panel.
   *
   * The selection is cleared before the host is told, so a host that draws the library BESIDE the
   * day — and therefore passes no `onInserted` — is still left with an empty foot rather than five
   * ticks over a day that already has them.
   */
  const addSelection = () => {
    if (daySlotToken === null || selection.length === 0) return;
    const outcome = addExercisesFromLibrary(daySlotToken, selection, role);
    if (outcome.kind === 'refused') {
      // The same refusal vocabulary a press and a drop already word — one rule, one sentence,
      // however it was reached. The selection stands, so the trainer can take some of it off and
      // try again rather than rebuild it.
      announce(t(`web:planBuilder.dnd.refusal.${outcome.reason}`));
      return;
    }
    announce(t(`${KEY}.insertedMany`, { count: outcome.exerciseSlotTokens.length }));
    clearSelection();
    // `inserted` over a non-empty selection always names at least one card, so the guard is a type
    // narrowing rather than a case: the operation refuses instead of inserting nothing.
    const first = outcome.exerciseSlotTokens[0];
    if (first !== undefined) onInserted?.(first);
  };

  /**
   * The middle rung of the drawer's Escape ladder: **a non-empty selection is what Escape undoes
   * first.**
   *
   * The whole ladder, from the inside out — the search field's own text, then the selection, then
   * the library, then the day. Each rung is a thing the trainer built and can regret, and Escape
   * takes the innermost one. Losing five chosen movements to the same key that puts the catalogue
   * away would make Escape the most expensive key on the surface.
   *
   * The innermost rung is the entity's: the field clears its own text and never lets the key out.
   *
   * Answers whether it consumed the key, so the two handlers that call it read as a ladder.
   */
  const escapeTookTheSelection = (): boolean => {
    if (selection.length === 0) return false;
    clearSelection();
    return true;
  };

  const insert = (item: ExerciseListItem) => {
    if (daySlotToken === null) return;
    const pick = libraryPick(item);

    // A reach aimed at ONE movement is a different act from an insert into a block, so it runs a
    // different operation — but out of the same gate and with the same refusal vocabulary, which is
    // why `insertWarmupFor` asks `libraryDropRefusal` itself rather than leaving the cap to a
    // caller. `runMoveCommand`'s "there is no second entry point" holds where it was written: it is
    // about the DRAG mechanics, and a mini-group is not one of them — nothing drags into it.
    if (warmupTargetSlotToken !== null) {
      const warmup = insertWarmupFor(warmupTargetSlotToken, pick);
      if (warmup.kind === 'refused') {
        announce(t(`web:planBuilder.dnd.refusal.${warmup.reason}`));
        return;
      }
      announce(t(`${KEY}.inserted`, { name: item.name }));
      onInserted?.(warmup.exerciseSlotToken);
      return;
    }

    const outcome = runMoveCommand({
      kind: 'insertFromLibrary',
      targetDaySlotToken: daySlotToken,
      // A press names no position, so it says so. `'end'` is the append the operation already
      // performs rather than a number this component would have to learn the day's block count to
      // compute — see `MoveCommand`.
      targetBlockIndex: 'end',
      pick,
      role,
    });

    // Every refusal is WORDED. It called `addExercise` directly until 7 September 2026, which is the
    // second entry point `runMoveCommand`'s header says must not exist: at the day's exercise cap it
    // returned in silence — the drawer stayed open, nothing appeared and nothing was said — and a
    // day the tree no longer held did the same. Both are worded now, out of the same gate the drop
    // asks. (Routing it here also asked the phase lock for one afternoon, which turned out to be a
    // question `libraryDropRefusal` should never have been asking — see its header.)
    if (outcome.kind === 'refused') {
      announce(t(`web:planBuilder.dnd.refusal.${outcome.reason}`));
      return;
    }
    if (outcome.kind !== 'inserted') return;
    announce(t(`${KEY}.inserted`, { name: item.name }));
    // `runMoveCommand` answers `refused` when the insert minted no token, so an `inserted` outcome
    // reaching here always names the card its press just made.
    if (outcome.exerciseSlotToken !== undefined) onInserted?.(outcome.exerciseSlotToken);
  };

  // `model/` may not import i18next, so the four facets are handed their words. Equipment is
  // absent: its chips carry backend DATA that arrives already localised.
  const facets: ExerciseFacetLabels = {
    group: (facet) => t(`${KEY}.facet.${facet}`),
    muscle: (muscle) => t(`exerciseLibrary.muscles.${muscle}`),
    type: (type) => t(`web:planBuilder.day.exerciseType.${type}`),
    origin: (origin) => t(`${KEY}.origin.${origin}`),
    everything: t(`${KEY}.anyValue`),
  };

  // Everything the shared library says on THIS surface. `entities/` carries no i18n namespace, so
  // the copy is resolved here and handed over — see `ExerciseLibraryListLabels`.
  const labels: ExerciseLibraryListLabels = {
    search: t(`${KEY}.search`),
    searchPlaceholder: t(`${KEY}.searchPlaceholder`),
    filters: t(`${KEY}.filters`),
    filtersWithCount: (count) => t(`${KEY}.filtersWithCount`, { count }),
    removeFilter: (label) => t(`${KEY}.removeFilter`, { label }),
    clearAll: t(`${KEY}.clearAll`),
    clearAllName: (count) => t(`${KEY}.clear`, { count }),
    count: (count) => t(`${KEY}.drawerCount`, { count }),
    results: (count) => t(`${KEY}.results`, { count }),
    loading: t(`${KEY}.loading`),
    error: t(`${KEY}.error`),
    retry: t(`${KEY}.retry`),
    empty: t(`${KEY}.empty`),
    noMatches: t(`${KEY}.noMatches`),
    noMatchesHint: t(`${KEY}.noMatchesHint`),
    otherLanguageFailed: t('web:exerciseLibrary.language.otherFailed'),
    rowAction: (name) => t(isSelecting ? `${KEY}.choose` : `${KEY}.insert`, { name }),
    muscle: (muscle) => t(`exerciseLibrary.muscles.${muscle}`),
    type: (type) => t(`web:planBuilder.day.exerciseType.${type}`),
    metaSeparator: t(`${KEY}.metaSeparator`),
    facets,
    panel: {
      tab: (tab) => t(`${KEY}.tab.${tab}`),
      tabWithCount: (label, count) => t(`${KEY}.tabWithCount`, { label, count }),
      panel: t(`${KEY}.filters`),
      optionName: (label, count) => t(`${KEY}.facetOption`, { label, count }),
      optionSearch: (facet) => t(`${KEY}.optionSearch.${facet}`),
      originHint: (id) => t(`${KEY}.originHint.${id}`),
      empty: t(`${KEY}.facetEmpty`),
      clear: t(`${KEY}.clearShort`),
      apply: t(`${KEY}.apply`),
    },
  };

  // The hovered movement, resolved out of the CURRENT results. See the peek's own note below.
  const peekItem = peek === null ? null : (results.find((item) => item.id === peek.id) ?? null);

  return (
    <aside
      className={`${String(styles.rail)} ${String(styles.drawer)}`}
      aria-label={t(`${KEY}.title`)}
      /* Escape from a ROW or from the foot, where the search field's own handler never sees it.
       * Stopping it here is what keeps the key off Base UI: the dialog's own dismissal is a React
       * `onKeyDown` on the popup ABOVE this element and a native listener on the `document` above
       * that, and a synthetic `stopPropagation` stops both — see `DayDock`'s header. Where there is
       * nothing chosen the key travels on, and the drawer closes exactly as it always has. */
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        if (escapeTookTheSelection()) event.stopPropagation();
      }}
    >
      <ExerciseLibraryList
        library={library}
        results={results}
        variant="drawer"
        labels={labels}
        trainer={{
          initials: displayInitials(user?.displayName ?? ''),
          avatarUrl: user?.avatarUrl ?? null,
        }}
        focusSignal={focusSignal}
        // A press CHOOSES on the drawer and inserts on the two surfaces beside a day. Where it
        // chooses, the ticks are what the list draws; where it inserts, there is nothing to mark.
        selectedIds={isSelecting ? selection.map((pick) => pick.id) : null}
        onPick={(item) => {
          if (isSelecting) choose(item);
          else insert(item);
        }}
        /* Rungs two and three of the ladder. The key is stopped HERE either way, because the compact
         * column beside the calendar's day editor has no drawer to close and its Escape would take
         * that whole dock down. The field's own text is rung one and never reaches this. */
        onSearchKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          event.stopPropagation();
          if (!escapeTookTheSelection()) onClose?.();
        }}
        heading={
          <>
            <p className={styles.eyebrow}>
              {warmupTargetName !== null
                ? t(`${KEY}.drawerEyebrowWarmup`, { name: warmupTargetName })
                : dayName === null
                  ? t(`${KEY}.drawerEyebrowNoDay`)
                  : t(`${KEY}.drawerEyebrow`, { day: dayName })}
            </p>
            <div className={styles.titleRow}>
              <h3 className={styles.title}>{t(`${KEY}.title`)}</h3>
              {onClose !== undefined && (
                <button
                  type="button"
                  className={styles.closeButton}
                  aria-label={t(`${KEY}.closeDrawer`)}
                  onClick={onClose}
                >
                  <span aria-hidden="true">×</span>
                </button>
              )}
            </div>
          </>
        }
        /* "Pasują do dnia Push A" — the mock's own caption, and in this app it is a CONTROL.
         *
         * The mock prints a sentence because its default cannot be got out of; a real library needs
         * a way to the whole catalogue that does not require guessing that ticking a filter widens
         * the list. So the sentence says what it says AND is the press that lifts the narrowing —
         * one affordance, in the slot the mock already spends on explaining the state. See
         * `model/dayAffinity`. */
        caption={
          isDayDefaultOn && dayName !== null ? (
            <button
              type="button"
              className={styles.dayFit}
              onClick={() => {
                setDayDefaultLifted(true);
              }}
            >
              {t(`${KEY}.dayFit`, { day: dayName })}
              <span className={styles.dayFitAction}>{t(`${KEY}.dayFitShowAll`)}</span>
            </button>
          ) : undefined
        }
        renderRow={(row) => (
          <RailDraggableRow
            row={{
              ...row,
              /* The peek stands at `calc(100% + 10px)` — past the library's right edge. In the
               * builder that lands over the day panel the drawer slid across; on the calendar's
               * dock, where the library unfolds to the LEFT, it lands over the day form beside it.
               * Both are the surface with room, which is the mock's own intent. */
              onHoverChange: (isHovering) => {
                setPeek(isHovering ? { id: row.item.id, stillFailed: false } : null);
              },
            }}
            // A row is draggable whenever a day is open AND the day is reachable, and the ZONE it
            // is released over decides which block it lands in — the main work at the position the
            // indicator shows, the warm-up appended, because that run draws no positions to land
            // between.
            //
            // It was additionally gated on `role === 'Main'` until 10 August 2026, which made
            // every row undraggable the moment a trainer aimed the rail at the warm-up: the rail
            // has no business deciding where a drag lands, because it cannot see where the
            // pointer went. `role` is now the PRESS path's target and nothing else.
            //
            // A library standing OVER the day has no zone to drop into at all — see
            // `overlaysTheDay`. There the press is the whole path, and drawing a grab cursor would
            // invite a gesture that always fails.
            canDrag={daySlotToken !== null && !overlaysTheDay}
          />
        )}
        onDragStart={() => {
          setPeek(null);
        }}
      />

      {/* The hover peek — the mock's card beside the hovered row, standing OUTSIDE the 320px drawer
        * over the panel next to it. A sibling of the library rather than a child of its scroller:
        * the list is the scroller, and a card inside a scrolling box would be clipped by it and
        * would ride the scroll. Rendered only while a row is hovered, so nothing decorative is
        * mounted the rest of the time and its entrance plays once per movement.
        *
        * The movement is looked up in the RESULTS rather than held in the state, so a peek cannot
        * outlive the list it came from: a filter typed while the pointer sits still narrows the
        * results, and a card left over from a movement no longer in them would be a description of
        * something not on screen. */}
      {peekItem !== null && (
        <RailPeek
          key={peekItem.id}
          item={peekItem}
          typeLabel={
            peekItem.typeKey === null
              ? null
              : t(`web:planBuilder.day.exerciseType.${peekItem.typeKey}`)
          }
          muscleLabel={(group) => t(`exerciseLibrary.muscles.${group}`)}
          levelLabel={(isPrimary) => t(`${KEY}.peekLevel.${isPrimary ? 'primary' : 'secondary'}`)}
          stillFailed={peek?.stillFailed ?? false}
          onStillFailed={() => {
            setPeek((current) => (current === null ? null : { ...current, stillFailed: true }));
          }}
        />
      )}

      {/* What this library actually does, printed — and it is not the same sentence at both widths,
        * because it is not the same act. The calendar's 240px column stands BESIDE the day, so a row
        * can be dragged onto it. The builder's drawer lies modally over the day: the form behind it
        * is `inert`, a dragged row is released onto nothing, and the mock's own library cards carry
        * a `draggable` attribute with no handler behind it. So the drawer says press, which is what
        * the drawer does. Each sentence stays one translatable string. */}
      <div className={styles.foot}>
        <p className={styles.hint}>
          {t(
            warmupTargetName !== null
              ? `${KEY}.hintDrawerWarmup`
              : isSelecting
                ? `${KEY}.hintDrawerSelect`
                : `${KEY}.hintDrawer`,
          )}
        </p>

        {/* The mock's own foot pair, and the `Wyczyść` appears only once there is something to
          * clear — beside an empty selection it would be a control for nothing. */}
        {isSelecting && selection.length > 0 && (
          <button type="button" className={styles.clearPicks} onClick={clearSelection}>
            {t(`${KEY}.clearShort`)}
          </button>
        )}
        {isSelecting && (
          <button
            type="button"
            className={`${String(inkButton.ink)} ${String(styles.addPicks)}`}
            /* Refused while nothing is chosen, and REFUSED rather than `disabled`: a control
              * carrying a reason has to stay in the tab order for the reason to be reachable at
              * all. The button has visible words, so the name CONTAINS them (WCAG 2.5.3) and the
              * reason is spoken after them — the same shape the day form's save and its superset
              * bar already use.
              *
              * The COUNT rides in the visible label and therefore in the name, so nothing here is
              * carried by the tick colour alone. */
            aria-label={
              selection.length === 0
                ? `${t(`${KEY}.addPicksEmpty`)}, ${t(`${KEY}.addPicksReason`)}`
                : undefined
            }
            aria-disabled={selection.length === 0 ? 'true' : undefined}
            title={
              selection.length === 0 ? t(`${KEY}.addPicksReason`) : t(`${KEY}.addPicks`, { count: selection.length })
            }
            onClick={addSelection}
          >
            {selection.length === 0
              ? t(`${KEY}.addPicksEmpty`)
              : t(`${KEY}.addPicks`, { count: selection.length })}
          </button>
        )}
      </div>
    </aside>
  );
}
